import React, {
  type Dispatch,
  type SetStateAction,
  useCallback,
  useContext,
} from "react";
import { styled } from "@mui/system";
import { MdOutlineMenuOpen, MdOutlineNewReleases } from "react-icons/md";
import { Tooltip, Typography, useTheme } from "@mui/material";
import { AnimatePresence, motion } from "framer-motion";
import { FlexCol, FlexRow } from "../../atoms/Flex/index";
import { Spacer } from "../../atoms/Spacer";
import { SidebarItem } from "./SideBarItem";
import {
  SIDEBAR_FOOTER_DATASOURCE,
  SideBarDataSourcesProps,
} from "../../../data/constants";
import { AuthContext } from "../../../context/AuthContext";
import { useNavigate } from "react-router-dom";
import { SideBarProfile } from "./SideBarProfile";
import { SUPER_ADMIN_PERMISSIONS } from "../../../data/roles";
import { UserRolesEnum } from "../../../typings";

const Footer = styled(FlexCol)`
  width: 100%;
`;

const LinkText = styled(motion.div)`
  white-space: nowrap;
  height: min-content;
`;

const Divider = styled(FlexRow)`
  border-bottom: 2px solid ${({ theme }) => theme.palette.colors["gray"]["200"]};
  width: 100%;
`;

const Item = styled(FlexRow, {
  shouldForwardProp: (props) => props !== "isSideBarOpen",
})<{ isSideBarOpen: boolean }>`
  align-items: center;
  justify-content: flex-start;
  column-gap: ${(props) => (props.isSideBarOpen ? "8px" : "0")};
  border-radius: 6px;
  row-gap: 10px;
  width: 100%;
  height: 36px;
  margin: 4px 0;
  padding: 8px;

  :hover {
    cursor: pointer;
    background-color: ${({ theme }) => theme.palette.colors["white"]["50"]};
    color: ${({ theme }) => theme.palette.colors["gray"]["600"]};
  }
`;

const ShowAnimation = {
  hidden: {
    width: 0,
    opacity: 0,
    transition: {
      duration: 0.2,
    },
  },
  show: {
    width: "auto",
    opacity: 1,
    transition: {
      duration: 0.2,
    },
  },
};

type SideBarFooterProps = {
  isOpen?: boolean;
  onClick: Dispatch<SetStateAction<boolean>>;
};

export const SideBarFooter: React.ComponentType<SideBarFooterProps> = ({
  isOpen,
  onClick,
}) => {
  const theme = useTheme();
  const auth = useContext(AuthContext);
  const navigate = useNavigate();

  const isSuperAdmin = SUPER_ADMIN_PERMISSIONS.includes(
    auth?.role as UserRolesEnum
  );

  const {
    palette: { colors },
  } = theme;

  const RenderItem = useCallback(
    ({ item }: { item: SideBarDataSourcesProps }) => {
      return <SidebarItem item={item} isOpen={isOpen} />;
    },
    [isOpen]
  );

  return (
    <Footer>
      <Tooltip title={!isOpen && "Expand"} placement="right">
        <Item
          isSideBarOpen={!!isOpen}
          onClick={() => {
            onClick(!isOpen);
            localStorage.setItem("isSidebarOpen", JSON.stringify(!isOpen));
          }}
        >
          <MdOutlineMenuOpen size={20} color={colors["grayGreen"]["600"]} />
          <AnimatePresence>
            {isOpen && (
              <LinkText
                variants={ShowAnimation}
                initial={"hidden"}
                animate={"show"}
                exit={"hidden"}
              >
                <Typography
                  variant={"subtitle2"}
                  color={colors["grayGreen"]["600"]}
                >
                  Collapse
                </Typography>
              </LinkText>
            )}
          </AnimatePresence>
        </Item>
      </Tooltip>
      <Spacer />
      <Divider />
      <Spacer />
      {SIDEBAR_FOOTER_DATASOURCE.filter((d) => {
        // Settings is super admin only
        if (d.activePath === "/settings" && !isSuperAdmin) return false;
        return true;
      }).map((d) => (
        <RenderItem key={d.activePath} item={d} />
      ))}
      <SideBarProfile isOpen={!!isOpen} />
    </Footer>
  );
};
